import type {
  MarketplaceProduct,
  PriceInsight,
  InventoryPrediction,
  MarketplaceReview,
  SentimentSummary,
} from '@/types';

/**
 * Mock multi-agent AI for the marketplace. Each function is structured so it
 * can later be swapped for a real model/endpoint (e.g. an `aiMarketplace`
 * callable) without changing call sites. Pure + deterministic where possible.
 */

const POSITIVE = ['great', 'love', 'excellent', 'amazing', 'best', 'fast', 'reliable', 'recommend', 'good', 'perfect', 'helpful', 'quality'];
const NEGATIVE = ['bad', 'slow', 'poor', 'broken', 'worst', 'late', 'buggy', 'refund', 'disappointed', 'terrible', 'awful', 'overpriced'];

export const aiMarketplaceService = {
  /**
   * Agent 1 — Personalized recommendations. Rule-based: prefer the same
   * industry/category as the seed product (or the user's industries), then
   * fill with popular items. Returns up to `count`.
   */
  recommend(
    all: MarketplaceProduct[],
    opts: { industryIds?: string[]; category?: string; excludeId?: string; count?: number } = {},
  ): MarketplaceProduct[] {
    const { industryIds = [], category, excludeId, count = 6 } = opts;
    const pool = all.filter((p) => p.id !== excludeId);
    const score = (p: MarketplaceProduct) =>
      (category && p.category === category ? 3 : 0) +
      (industryIds.includes(p.industryId) ? 2 : 0) +
      Math.min(2, p.stats.averageRating / 2.5);
    return [...pool].sort((a, b) => score(b) - score(a) || b.stats.purchases - a.stats.purchases).slice(0, count);
  },

  /** Agent 2 — Dynamic pricing. Compares price to the category market average. */
  priceInsight(product: MarketplaceProduct, all: MarketplaceProduct[]): PriceInsight {
    const peers = all.filter((p) => p.category === product.category && p.price > 0);
    const marketAverage = peers.length
      ? Math.round(peers.reduce((s, p) => s + p.price, 0) / peers.length)
      : product.price;
    const delta = marketAverage === 0 ? 0 : (product.price - marketAverage) / marketAverage;
    if (delta <= -0.1) return { verdict: 'below-market', marketAverage, message: `Priced ${Math.round(-delta * 100)}% below the category average — strong value.` };
    if (delta >= 0.1) return { verdict: 'above-market', marketAverage, message: `Priced ${Math.round(delta * 100)}% above the category average.` };
    return { verdict: 'fair', marketAverage, message: 'In line with the market average for its category.' };
  },

  /** Agent 3 — Inventory prediction from recent daily sales. */
  predictInventory(product: MarketplaceProduct): InventoryPrediction {
    if (product.type === 'service' || product.stock == null) {
      return { daysRemaining: null, lowStock: false, message: product.type === 'service' ? 'Service — always available.' : 'Stock not tracked.' };
    }
    const sales = product.recentDailySales ?? [];
    const avgDaily = sales.length ? sales.reduce((s, n) => s + n, 0) / sales.length : 0;
    if (avgDaily <= 0) return { daysRemaining: null, lowStock: product.stock < 5, message: product.stock < 5 ? 'Low stock.' : 'Healthy stock.' };
    const daysRemaining = Math.floor(product.stock / avgDaily);
    return {
      daysRemaining,
      lowStock: daysRemaining <= 7,
      message: daysRemaining <= 7 ? `Low stock — predicted to sell out in ~${daysRemaining} days.` : `~${daysRemaining} days of stock at current demand.`,
    };
  },

  /** Agent 5 — Review sentiment from keyword counts. */
  sentiment(reviews: MarketplaceReview[]): SentimentSummary {
    let positive = 0;
    let negative = 0;
    for (const r of reviews) {
      const text = r.comment.toLowerCase();
      positive += POSITIVE.filter((w) => text.includes(w)).length;
      negative += NEGATIVE.filter((w) => text.includes(w)).length;
    }
    const total = positive + negative;
    const score = total === 0 ? 0 : (positive - negative) / total;
    const label = score > 0.2 ? 'positive' : score < -0.2 ? 'negative' : 'mixed';
    return { score, label, positive, negative };
  },
};
